import React, { useEffect, useRef, useState } from "react";

type GoldenCursorProps = {
  radius?: number;
  dotSize?: number;
  lag?: number;
  hideSystemCursor?: boolean;
};

export function GoldenCursor({
  radius = 240,
  dotSize = 16,
  lag = 1,
  hideSystemCursor = true,
}: GoldenCursorProps) {
  const target = useRef({
    x: -1000,
    y: -1000,
  });

  const current = useRef({
    x: -1000,
    y: -1000,
  });

  const raf = useRef<number | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  const [clicking, setClicking] = useState(false);
  const [hoveringClickable, setHoveringClickable] = useState(false);
  const [cursorHidden, setCursorHidden] = useState(false);

  const hoveringClickableRef = useRef(false);
  const cursorHiddenRef = useRef(false);

  useEffect(() => {
    const isCoarse =
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches;

    if (isCoarse) return;

    const interactiveSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[role="button"]',
      '[role="link"]',
      "[onclick]",
      ".cursor-pointer",
      '[data-cursor="interactive"]',
    ].join(",");

    const hideCursorSelector = '[data-cursor="hide"]';

    const detectClickable = (element: EventTarget | null) => {
      if (!(element instanceof Element)) return false;

      return Boolean(element.closest(interactiveSelector));
    };

    const detectHideCursor = (element: EventTarget | null) => {
      if (!(element instanceof Element)) return false;

      return Boolean(element.closest(hideCursorSelector));
    };

    const updateClickableState = (value: boolean) => {
      if (hoveringClickableRef.current === value) return;

      hoveringClickableRef.current = value;
      setHoveringClickable(value);
    };

    const updateHiddenState = (value: boolean) => {
      if (cursorHiddenRef.current === value) return;

      cursorHiddenRef.current = value;
      setCursorHidden(value);
    };

    const onMove = (e: PointerEvent) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;

      const shouldHide = detectHideCursor(e.target);

      updateHiddenState(shouldHide);

      // Si debe ocultarse, no necesitamos activar
      // el efecto de elemento interactivo.
      if (shouldHide) {
        updateClickableState(false);
        return;
      }

      updateClickableState(detectClickable(e.target));
    };

    const onPointerOver = (e: PointerEvent) => {
      const shouldHide = detectHideCursor(e.target);

      updateHiddenState(shouldHide);

      if (shouldHide) {
        updateClickableState(false);
        return;
      }

      updateClickableState(detectClickable(e.target));
    };

    const onPointerOut = (e: PointerEvent) => {
      const nextTarget = e.relatedTarget;

      if (!(nextTarget instanceof Element)) {
        updateHiddenState(false);
        updateClickableState(false);
        return;
      }

      const shouldHide = Boolean(
        nextTarget.closest(hideCursorSelector)
      );

      updateHiddenState(shouldHide);

      if (shouldHide) {
        updateClickableState(false);
        return;
      }

      updateClickableState(
        Boolean(nextTarget.closest(interactiveSelector))
      );
    };

    const onLeave = () => {
      target.current.x = -1000;
      target.current.y = -1000;

      updateClickableState(false);
      updateHiddenState(false);
      setClicking(false);
    };

    const onDown = () => {
      setClicking(true);
    };

    const onUp = () => {
      setClicking(false);
    };

    window.addEventListener("pointermove", onMove, {
      passive: true,
    });

    window.addEventListener("pointerover", onPointerOver, {
      passive: true,
    });

    window.addEventListener("pointerout", onPointerOut, {
      passive: true,
    });

    window.addEventListener("pointerleave", onLeave, {
      passive: true,
    });

    window.addEventListener("pointerdown", onDown, {
      passive: true,
    });

    window.addEventListener("pointerup", onUp, {
      passive: true,
    });

    const loop = () => {
      const c = current.current;
      const t = target.current;

      c.x += (t.x - c.x) * lag;
      c.y += (t.y - c.y) * lag;

      if (elRef.current) {
        elRef.current.style.setProperty("--cx", `${c.x}px`);
        elRef.current.style.setProperty("--cy", `${c.y}px`);
        elRef.current.style.setProperty("--r", `${radius}px`);
        elRef.current.style.setProperty("--dot", `${dotSize}px`);
      }

      raf.current = requestAnimationFrame(loop);
    };

    raf.current = requestAnimationFrame(loop);

    if (hideSystemCursor) {
      document.body.classList.add("cursor-hidden");
    }

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerover", onPointerOver);
      window.removeEventListener("pointerout", onPointerOut);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);

      if (raf.current) {
        cancelAnimationFrame(raf.current);
      }

      if (hideSystemCursor) {
        document.body.classList.remove("cursor-hidden");
      }
    };
  }, [lag, radius, dotSize, hideSystemCursor]);

  return (
    <div
      ref={elRef}
      className={[
        "golden-cursor",
        hoveringClickable ? "is-interactive" : "",
        clicking ? "is-clicking" : "",
        cursorHidden ? "is-hidden" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
    />
  );
}

